import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AppShell } from '@/components/nav/AppShell';
import { customersApi, authExtraApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { fmtDate, fmtMoney, fmtDocNumber } from '@/lib/format';
import { JobStatusPill } from '@/components/jobs/JobStatusPill';
import { Mail, Phone, MapPin, Home as HomeIcon, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { ADMIN_PORTAL } from '@/constants/testIds';

export default function CustomerDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);

  useEffect(() => { customersApi.get(id).then(setC); }, [id]);

  const invitePortal = async () => {
    try {
      const r = await authExtraApi.invitePortal(id);
      setInviteResult(r);
      toast.success('Portal invitation created (mocked email in Inbox)');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to invite');
    }
  };

  if (!c) return <AppShell title="Customer"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;

  return (
    <AppShell title={c.name}>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold">{c.name}</h1>
            <div className="mt-1 flex flex-wrap gap-3 text-sm text-muted-foreground">
              {c.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>}
              {c.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
              {c.service_address && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.service_address}</span>}
            </div>
            {c.tags && c.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {c.tags.map(t => <Badge key={t} variant="outline">{t}</Badge>)}
              </div>
            )}
          </div>
          <Card>
            <CardContent className="p-4 min-w-[200px] space-y-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-medium">Lifetime value</div>
                <div className="font-display text-2xl font-semibold tabular-nums mt-1">{fmtMoney(c.ltv || 0)}</div>
              </div>
              <Button size="sm" variant="outline" onClick={invitePortal} className="w-full" data-testid={ADMIN_PORTAL.inviteButton}>
                <UserPlus className="h-3 w-3 mr-1" />Invite to portal
              </Button>
              {inviteResult && (
                <div className="text-[11px] rounded-md bg-muted p-2 space-y-1">
                  <div>Temp password: <span className="font-mono font-semibold">{inviteResult.temp_password}</span></div>
                  <div className="text-muted-foreground">Sent to {inviteResult.email}. Login link: <a href="/portal/login" className="text-primary hover:underline">/portal/login</a></div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="jobs">
          <TabsList>
            <TabsTrigger value="jobs" data-testid="customer-tab-jobs">Jobs ({c.jobs?.length || 0})</TabsTrigger>
            <TabsTrigger value="properties" data-testid="customer-tab-properties">Properties ({c.properties?.length || 0})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="customer-tab-invoices">Invoices ({c.invoices?.length || 0})</TabsTrigger>
            <TabsTrigger value="estimates" data-testid="customer-tab-estimates">Estimates ({c.estimates?.length || 0})</TabsTrigger>
            <TabsTrigger value="payments" data-testid="customer-tab-payments">Payments ({c.payments?.length || 0})</TabsTrigger>
          </TabsList>

          <TabsContent value="jobs" className="mt-4">
            <div className="space-y-2">
              {(c.jobs || []).map(j => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="flex items-center justify-between border rounded-lg p-3 bg-card hover:bg-muted/40">
                  <div>
                    <div className="font-medium text-sm">Job {j.id.slice(0, 6)}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(j.scheduled_at)}</div>
                  </div>
                  <JobStatusPill status={j.status} />
                </Link>
              ))}
              {(!c.jobs || c.jobs.length === 0) && <div className="text-sm text-muted-foreground">No jobs yet.</div>}
            </div>
          </TabsContent>

          <TabsContent value="properties" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(c.properties || []).map(p => (
                <Link key={p.id} to={`/properties/${p.id}`} className="block">
                  <Card className="hover:border-primary/50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 font-medium"><HomeIcon className="h-4 w-4 text-primary" />{p.nickname}</div>
                      <div className="text-xs text-muted-foreground mt-1">{p.address || '—'}</div>
                      <div className="mt-2 text-xs text-muted-foreground">{p.bedrooms} bd · {p.bathrooms} ba · {p.sqft ? `${p.sqft} sqft` : '—'}</div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
              {(!c.properties || c.properties.length === 0) && <div className="text-sm text-muted-foreground">No properties yet.</div>}
            </div>
          </TabsContent>

          <TabsContent value="invoices" className="mt-4">
            <div className="space-y-2">
              {(c.invoices || []).map(inv => (
                <div key={inv.id} className="flex items-center justify-between border rounded-lg p-3 bg-card">
                  <div>
                    <div className="font-medium text-sm">{fmtDocNumber(inv, 'Invoice')}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(inv.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold tabular-nums">{fmtMoney(inv.total)}</div>
                    <Badge variant="outline" className="text-[10px]">{inv.status}</Badge>
                  </div>
                </div>
              ))}
              {(!c.invoices || c.invoices.length === 0) && <div className="text-sm text-muted-foreground">No invoices.</div>}
            </div>
          </TabsContent>

          <TabsContent value="estimates" className="mt-4">
            <div className="space-y-2">
              {(c.estimates || []).map(e => (
                <div key={e.id} className="flex items-center justify-between border rounded-lg p-3 bg-card">
                  <div>
                    <div className="font-medium text-sm">{fmtDocNumber(e, 'Estimate')}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(e.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold tabular-nums">{fmtMoney(e.total)}</div>
                    <Badge variant="outline" className="text-[10px]">{e.status}</Badge>
                  </div>
                </div>
              ))}
              {(!c.estimates || c.estimates.length === 0) && <div className="text-sm text-muted-foreground">No estimates.</div>}
            </div>
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            <div className="space-y-2">
              {(c.payments || []).map(p => (
                <div key={p.id} className="flex items-center justify-between border rounded-lg p-3 bg-card">
                  <div>
                    <div className="font-medium text-sm">{p.method}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</div>
                  </div>
                  <div className="font-semibold tabular-nums">{fmtMoney(p.amount)}</div>
                </div>
              ))}
              {(!c.payments || c.payments.length === 0) && <div className="text-sm text-muted-foreground">No payments recorded.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
